import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import uuid4 from 'uuid4';

import { uiConfig } from '../../../../../../../../conf/predicatePartRels';

// Components
import Form from '../../../../../../../../../components/Form';
import { FormField } from '../../../../../../../../../components/Form/components/FormField';
import { TextField } from '../../../../../../../../../components/TextField';
import { MaterialIcon } from '../../../../../../../../../components/MaterialIcon';
import { Spinner } from '../../../../../../../../../components/Spinner';
import { CodeSystemTableModal } from './components/CodeSystemTableModal/index.js';
import { ValueSetTableModal } from './components/ValueSetTableModal';
import { ConceptTableModal } from './components/ConceptTableModal';
import { CodesTableModal } from './components/CodesTableModal';
import { ListsTableModal } from './components/ListsTableModal';
import { CodeSelectionTable } from './components/CodeSelectionTable';
import { ConceptSelectionTable } from './components/ConceptSelectionTable';

// Context
import { CriteriaStoreContext } from '../../../../../../../state/criteria/context';

// Enums
import { dtoState } from '../../../../../../../../../common/enums/dtoState';

// Hooks
import { useGetPredicatePartRelsMetadata } from '../../../../../../../api/hooks/useGetPredicatePartRelsMetadata';

// Util
import { translateUIConfigForAddEdit } from '../../../../../../../../../common/utility';

const DrawerRow = `
    padding: 1rem;
    display: flex;
    flex-direction: row;
`;

export const DrawerHeader = styled.div`
    ${DrawerRow};
    justify-content: space-between;
`;

export const DrawerFooter = styled.div`
    ${DrawerRow};
    justify-content: flex-end;
`;

const constraintSourcePathsByType = {
    Code: {
        dto: 'cdsCodeDTO',
        id: 'codeId'
    },
    CodeSystem: {
        dto: 'cdsCodeSystemDTO',
        id: 'codeSystemId'
    },
    Concept: {
        dto: 'openCdsConceptDTO',
        id: 'codeId'
    },
    List: {
        dto: 'cdsListDTO',
        id: 'listId'
    },
    ValueSet: {
        dto: 'valueSetDTO',
        id: 'valueSetId'
    }
};

export const PredicatePartRelForm = React.forwardRef(
    (
        {
            data,
            partId,
            setEdited,
            conceptSelectionType,
            saveDisabled,
            clearSelectedConcepts,
            selectedConcepts,
            applyPredicatePartConceptsUpdate
        },
        ref
    ) => {
        const FormRef = React.useRef(null);
        const { sessionToken } = React.useContext(CriteriaStoreContext);
        const [showModal, setShowModal] = React.useState(null);
        const [errors, setErrors] = React.useState({});
        const [constraintType, setConstraintType] = React.useState(null);
        const [predicatePartRel, setPredicatePartRel] = React.useState(null);
        const formModel = React.useMemo(() => {
            return translateUIConfigForAddEdit(uiConfig);
        }, []);
        const isNew = false;
        const predicatePartRelsMetadataQuery = useGetPredicatePartRelsMetadata(sessionToken);
        const [compose, updateCompose] = React.useState(false);

        const updateContraintType = React.useCallback(
            (newConstraintType, isInit = false) => {
                setConstraintType(newConstraintType);
                if (!isInit) {
                    clearSelectedConcepts();
                }
            },
            [setConstraintType, clearSelectedConcepts]
        );

        React.useEffect(() => {
            if (data) {
                setPredicatePartRel(data);
            } else {
                setPredicatePartRel({ ...uiConfig.newDoc, uuid: uuid4() });
            }
        }, [data, setPredicatePartRel]);

        const prepareOptions = () => {
            let optionsOverride = {};
            optionsOverride.constraintType = predicatePartRelsMetadataQuery?.data?.constraintType;

            return optionsOverride;
        };

        let optionsOverride = prepareOptions();

        for (let key in optionsOverride) {
            let subIndex = formModel.findIndex((v) => v.accessor === key);
            if (subIndex !== -1) {
                formModel[subIndex].options = optionsOverride[key];
            }
        }

        const collectionQueryParam = React.useMemo(() => {
            const pathConfig = constraintSourcePathsByType[constraintType];

            return constraintType && pathConfig ? predicatePartRel?.[pathConfig.dto]?.[pathConfig.id] : null;
        }, [
            constraintType,
            predicatePartRel?.cdsCodeDTO,
            predicatePartRel?.cdsCodeSystemDTO,
            predicatePartRel?.openCdsConceptDTO,
            predicatePartRel?.cdsListDTO,
            predicatePartRel?.valueSetDTO
        ]);

        const validate = (formState) => {
            let newErrors = {};
            for (let field of formModel) {
                let { valid, failed } = field.validation(formState[field.accessor]);
                if (!valid) {
                    newErrors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
                }
                // Add additional validation here;
            }
            // Add relational validation here

            setErrors(newErrors);
            let valid = Object.keys(newErrors).length === 0;
            return { valid, failureMsg: '' };
        };

        React.useImperativeHandle(
            ref,
            () => ({
                clearSelection: () => {
                    return validate({
                        ...(predicatePartRel ?? {}),
                        ...FormRef?.current?.getFormState?.()
                    });
                },
                getFormState: () => {
                    return {
                        ...(predicatePartRel ?? {}),
                        ...FormRef?.current?.getFormState?.()
                    };
                }
            }),
            [FormRef, constraintType, predicatePartRel]
        );

        const selectConstraintSource = React.useCallback(
            (selectedConstraintType, rowData) => {
                const pathConfig = constraintSourcePathsByType[selectedConstraintType];

                if (!pathConfig) return;
                if (rowData?.[pathConfig.id] !== predicatePartRel?.[pathConfig.dto]?.[pathConfig.id]) {
                    clearSelectedConcepts?.();
                }
                setPredicatePartRel({
                    ...predicatePartRel,
                    dtoState: predicatePartRel.dtoState === dtoState.New ? dtoState.New : dtoState.Updated,
                    [pathConfig.dto]: {
                        ...rowData
                    }
                });
                setEdited(true);
                setShowModal(false);
            },
            [predicatePartRel, setEdited, setShowModal, setPredicatePartRel, clearSelectedConcepts]
        );

        return predicatePartRel !== null ? (
            <>
                <Form
                    formModel={formModel}
                    isNew={isNew}
                    data={predicatePartRel}
                    errors={errors}
                    compose={compose}
                    onApplyFormState={() => {}}
                    setEdited={setEdited}
                    // fatalError={fatalError}
                    ref={FormRef}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : undefined}
                    menuPortalDocument
                    compact
                    hookFxns={{
                        updateConstraintType: updateContraintType
                    }}
                />

                {constraintType === 'Code' ? (
                    <>
                        <FormField
                            label={'Constraint Source'}
                            labelFor={'constraint-source-cdsCodeDTO'}
                            readOnly={true}
                            compact={true}
                            size="sm"
                            error={errors?.cdsCodeDTO}
                        >
                            <TextField
                                id="constraint-source-cdsCodeDTO"
                                name="constraint-source-cdsCodeDTO"
                                style={{ cursor: 'pointer' }}
                                inputProps={{
                                    disabled: false,
                                    readOnly: false,
                                    onClick: () => {
                                        setShowModal(true);
                                    }
                                }}
                                value={predicatePartRel?.cdsCodeDTO?.displayName ?? ''}
                                iconRight={<MaterialIcon icon={'search'} />}
                                error={errors?.cdsCodeDTO || undefined}
                            />
                        </FormField>
                        {showModal ? (
                            <CodesTableModal
                                closeDialog={() => setShowModal(false)}
                                onSelect={selectConstraintSource.bind(null, 'Code')}
                            />
                        ) : null}
                    </>
                ) : null}
                {constraintType === 'CodeSystem' ? (
                    <>
                        <FormField
                            label={'Constraint Source'}
                            labelFor={'constraint-source-cdsCodeSystemDTO'}
                            readOnly={true}
                            compact={true}
                            size="sm"
                            error={errors?.cdsCodeSystemDTO ?? ''}
                        >
                            <TextField
                                id="constraint-source-cdsCodeSystemDTO"
                                name="constraint-source-cdsCodeSystemDTO"
                                style={{ cursor: 'pointer' }}
                                inputProps={{
                                    disabled: false,
                                    readOnly: false,
                                    onClick: () => {
                                        setShowModal(true);
                                    }
                                }}
                                value={predicatePartRel?.cdsCodeSystemDTO?.name ?? ''}
                                iconRight={<MaterialIcon icon={'search'} />}
                                error={errors?.cdsCodeSystemDTO || ''}
                            />
                        </FormField>
                        {showModal ? (
                            <CodeSystemTableModal
                                closeDialog={() => setShowModal(false)}
                                onSelect={selectConstraintSource.bind(null, 'CodeSystem')}
                            />
                        ) : null}
                    </>
                ) : null}
                {constraintType === 'Concept' ? (
                    <>
                        <FormField
                            label={'Constraint Source'}
                            labelFor={'constraint-source-openCdsConceptDTO'}
                            readOnly={true}
                            compact={true}
                            size="sm"
                            error={errors?.openCdsConceptDTO ?? ''}
                        >
                            <TextField
                                id="constraint-source-openCdsConceptDTO"
                                name="constraint-source-openCdsConceptDTO"
                                style={{ cursor: 'pointer' }}
                                inputProps={{
                                    disabled: false,
                                    readOnly: false,
                                    onClick: () => {
                                        setShowModal(true);
                                    }
                                }}
                                value={predicatePartRel?.openCdsConceptDTO?.displayName ?? ''}
                                iconRight={<MaterialIcon icon={'search'} />}
                                error={errors?.openCdsConceptDTO || ''}
                            />
                        </FormField>
                        {showModal ? (
                            <ConceptTableModal
                                closeDialog={() => setShowModal(false)}
                                onSelect={selectConstraintSource.bind(null, 'Concept')}
                            />
                        ) : null}
                    </>
                ) : null}
                {constraintType === 'List' ? (
                    <>
                        <FormField
                            label={'Constraint Source'}
                            labelFor={'constraint-source-cdsListDTO'}
                            readOnly={true}
                            compact={true}
                            size="sm"
                            error={errors?.cdsListDTO ?? ''}
                        >
                            <TextField
                                id="constraint-source-cdsListDTO"
                                name="constraint-source-cdsListDTO"
                                style={{ cursor: 'pointer' }}
                                inputProps={{
                                    disabled: false,
                                    readOnly: false,
                                    onClick: () => {
                                        setShowModal(true);
                                    }
                                }}
                                value={predicatePartRel?.cdsListDTO?.name ?? ''}
                                iconRight={<MaterialIcon icon={'search'} />}
                                error={errors?.cdsListDTO || ''}
                            />
                        </FormField>
                        {showModal ? (
                            <ListsTableModal
                                closeDialog={() => setShowModal(false)}
                                onSelect={selectConstraintSource.bind(null, 'List')}
                            />
                        ) : null}
                    </>
                ) : null}
                {constraintType === 'ValueSet' ? (
                    <>
                        <FormField
                            label={'Constraint Source'}
                            labelFor={'constraint-source-valueSetDTO'}
                            readOnly={true}
                            compact={true}
                            size="sm"
                            error={errors?.valueSetDTO}
                        >
                            <TextField
                                id="constraint-source-valueSetDTO"
                                name="constraint-source-valueSetDTO"
                                style={{ cursor: 'pointer' }}
                                inputProps={{
                                    disabled: false,
                                    readOnly: false,
                                    onClick: () => {
                                        setShowModal(true);
                                    }
                                }}
                                value={predicatePartRel?.valueSetDTO?.name ?? ''}
                                iconRight={<MaterialIcon icon={'search'} />}
                                error={errors?.valueSetDTO || undefined}
                            />
                        </FormField>
                        {showModal ? (
                            <ValueSetTableModal
                                closeDialog={() => setShowModal(false)}
                                onSelect={selectConstraintSource.bind(null, 'ValueSet')}
                            />
                        ) : null}
                    </>
                ) : null}
                {conceptSelectionType === 'Code' && constraintType ? (
                    <FormField
                        label={'Code Selection'}
                        labelFor={'code-selection-table'}
                        readOnly={true}
                        compact={true}
                        size="sm"
                        // error={errors?.valueSetDTO}
                    >
                        <CodeSelectionTable
                            partId={partId}
                            constraintType={constraintType}
                            queryParam={collectionQueryParam}
                            selectedConcepts={selectedConcepts}
                            applyPredicatePartConceptsUpdate={applyPredicatePartConceptsUpdate}
                        />
                    </FormField>
                ) : null}
                {conceptSelectionType === 'Concept' && constraintType ? (
                    <FormField
                        label={'Concept Selection'}
                        labelFor={'concept-selection-table'}
                        readOnly={true}
                        compact={true}
                        size="sm"
                        // error={errors?.valueSetDTO}
                    >
                        <ConceptSelectionTable
                            partId={partId}
                            constraintType={constraintType}
                            queryParam={collectionQueryParam}
                            selectedConcepts={selectedConcepts}
                            applyPredicatePartConceptsUpdate={applyPredicatePartConceptsUpdate}
                        />
                    </FormField>
                ) : null}
            </>
        ) : (
            <Spinner />
        );
    }
);
PredicatePartRelForm.displayName = 'PredicatePartRelForm';
export default PredicatePartRelForm;

PredicatePartRelForm.propTypes = {
    data: PropTypes.object,
    setEdited: PropTypes.func,
    saveDisabled: PropTypes.bool,
    conceptSelectionType: PropTypes.string,
    partId: PropTypes.string,
    selectedConcepts: PropTypes.arrayOf(PropTypes.object),
    clearSelectedConcepts: PropTypes.func,
    applyPredicatePartConceptsUpdate: PropTypes.func
};
